import React from 'react';
import T from 'i18n';
import {
  AddRounded,
  AttachFile,
  Bolt,
  CloseRounded,
  Compare,
  LibraryBooks,
  Psychology,
  SendRounded,
  StopRounded,
  TextFormat,
} from '@mui/icons-material';
import { CircularProgress, Switch as MuiSwitch, TextField } from '@mui/material';
import type { ResponseMode } from '../constants';
import styles from '../AskAISSE.module.scss';

export type QuickActionKey = 'bundles' | 'documents' | 'prompt';

type Props = {
  fileSidebarAskAI: boolean;
  showResponseArea: boolean;
  isLoading?: boolean;
  inputValue: string;
  setInputValue: (val: string) => void;
  inputRef: React.RefObject<HTMLInputElement>;
  onSend: () => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  isStreaming: boolean;
  onStopStreaming: () => void;

  showQuickActionsMenuTrigger?: boolean;
  menuOpen: boolean;
  onMenuToggle: (event: React.MouseEvent<HTMLButtonElement>) => void;

  selectedOutputFormat: string | null;
  outputFormatLabel: string;
  onClearOutputFormat: (event: React.MouseEvent<HTMLButtonElement>) => void;

  selectedFilesCount: number;
  documentsChipLabel: string;
  onOpenDocuments: () => void;
  onClearSelectedFiles: (event: React.MouseEvent<HTMLButtonElement>) => void;

  selectedBundlesCount: number;
  bundlesChipLabel: string;
  bundlesAnchorRef: React.RefObject<HTMLSpanElement>;
  onOpenBundlesMenu: () => void;
  onClearSelectedBundles: (event: React.MouseEvent<HTMLButtonElement>) => void;

  onOpenPromptDialog: () => void;

  compareFilesCount?: number;
  compareChipLabel?: string;
  onOpenCompare?: () => void;
  onClearCompareFiles?: (event: React.MouseEvent<HTMLButtonElement>) => void;

  responseMode?: ResponseMode;
  onResponseModeChange?: (mode: ResponseMode) => void;
  showStandingInstructionsToggle?: boolean;
  useStandingInstructions?: boolean;
  onUseStandingInstructionsChange?: (checked: boolean) => void;
};

export default function PromptComposer(props: Props) {
  const {
    fileSidebarAskAI,
    showResponseArea,
    inputValue,
    setInputValue,
    inputRef,
    onSend,
    onSubmit,
    isStreaming,
    onStopStreaming,

    showQuickActionsMenuTrigger = true,
    menuOpen,
    onMenuToggle,

    selectedOutputFormat,
    outputFormatLabel,
    onClearOutputFormat,

    selectedFilesCount,
    documentsChipLabel,
    onOpenDocuments,
    onClearSelectedFiles,

    onOpenPromptDialog,

    compareFilesCount = 0,
    compareChipLabel = '',
    onOpenCompare,
    onClearCompareFiles,

    responseMode = 'thinking',
    onResponseModeChange,
    showStandingInstructionsToggle = false,
    useStandingInstructions = false,
    onUseStandingInstructionsChange,
  } = props;

  const formClassName = [
    styles.promptShell,
    styles.promptShellMultiline,
    fileSidebarAskAI ? styles.promptShellSidebar : '',
    showResponseArea ? styles.promptShellSticky : '',
    showResponseArea && fileSidebarAskAI ? styles.promptShellStickySidebar : '',
  ]
    .filter(Boolean)
    .join(' ');

  const loaderClassName = [
    styles.promptLoaderOnly,
    fileSidebarAskAI ? styles.promptShellSidebar : '',
    showResponseArea ? styles.promptShellSticky : '',
    showResponseArea && fileSidebarAskAI ? styles.promptShellStickySidebar : '',
  ]
    .filter(Boolean)
    .join(' ');

  const hasFilesFilter = selectedFilesCount > 0;
  const hasCompareFilter = compareFilesCount > 0;
  const documentsChip = hasFilesFilter ? (
    <div
      className={styles.outputFormatChip}
      role="button"
      tabIndex={0}
      aria-label="Selected documents"
      style={{ cursor: 'pointer' }}
      onClick={onOpenDocuments}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpenDocuments();
        }
      }}
    >
      <button
        type="button"
        className={styles.outputFormatIconButton}
        aria-label="Clear selected documents"
        onClick={onClearSelectedFiles}
      >
        <span className={styles.outputFormatIconDefault} aria-hidden="true">
          <AttachFile fontSize="small" />
        </span>
        <span className={styles.outputFormatIconHover} aria-hidden="true">
          <CloseRounded fontSize="inherit" />
        </span>
      </button>
      <span className={styles.outputFormatChipText}>{documentsChipLabel}</span>
    </div>
  ) : null;
  const documentsButton = (
    <button type="button" className={styles.quickActionButton} onClick={onOpenDocuments}>
      <AttachFile fontSize="small" />
      <span>{T.translate('case.documentsOrBundles')}</span>
    </button>
  );
  const compareChip = hasCompareFilter ? (
    <div
      className={styles.outputFormatChip}
      role="button"
      tabIndex={0}
      aria-label="Selected compare files"
      style={{ cursor: 'pointer' }}
      onClick={onOpenCompare}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpenCompare?.();
        }
      }}
    >
      <button
        type="button"
        className={styles.outputFormatIconButton}
        aria-label="Clear compare files"
        onClick={onClearCompareFiles}
      >
        <span className={styles.outputFormatIconDefault} aria-hidden="true">
          <Compare fontSize="small" />
        </span>
        <span className={styles.outputFormatIconHover} aria-hidden="true">
          <CloseRounded fontSize="inherit" />
        </span>
      </button>
      <span className={styles.outputFormatChipText}>{compareChipLabel}</span>
    </div>
  ) : null;

  if (props.isLoading) {
    return (
      <div className={loaderClassName} aria-busy="true" aria-live="polite">
        <CircularProgress size={48} />
      </div>
    );
  }
  return (
    <form
      className={formClassName}
      onSubmit={(event) => {
        if (isStreaming) {
          event.preventDefault();
          return;
        }
        onSubmit(event);
      }}
    >
      <div className={styles.inputWrapper}>
        <TextField
          inputRef={inputRef}
          variant="standard"
          fullWidth
          multiline
          maxRows={8}
          placeholder={T.translate('case.topicInputPlaceholder')}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(event) => {
            // Allow Shift+Enter for new line
            if (event.key === 'Enter' && event.shiftKey) {
              return;
            }
            // Handle Enter key for submission
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              if (isStreaming) {
                return;
              }
              onSend();
            }
          }}
          autoComplete="off"
          InputProps={{
            disableUnderline: true,
            sx: { fontSize: '16px' },
          }}
          sx={{
            padding: 0,
            '& .MuiInputBase-root': {
              overflow: 'visible',
              padding: 0,
            },
            '& .MuiInputBase-inputMultiline': {
              resize: 'none',
              lineHeight: 1.5,
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '10px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#c1c1c1',
                borderRadius: '10px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#a8a8a8',
              },
            },
          }}
        />
      </div>

      <div className={styles.buttonRow}>
        {showQuickActionsMenuTrigger && (
          <button
            type="button"
            aria-label="Open quick actions"
            aria-expanded={menuOpen}
            className={styles.iconButton}
            onClick={onMenuToggle}
          >
            <AddRounded fontSize="small" />
          </button>
        )}

        {selectedOutputFormat && (
          <div className={styles.outputFormatChip} aria-label="Selected answer format">
            <button
              type="button"
              className={styles.outputFormatIconButton}
              aria-label="Clear answer format"
              onClick={onClearOutputFormat}
            >
              <span className={styles.outputFormatIconDefault} aria-hidden="true">
                <TextFormat fontSize="small" />
              </span>
              <span className={styles.outputFormatIconHover} aria-hidden="true">
                <CloseRounded fontSize="inherit" />
              </span>
            </button>
            <span className={styles.outputFormatChipText}>{outputFormatLabel}</span>
          </div>
        )}

        {!fileSidebarAskAI && (
          <div className={styles.quickActions}>
            {/* Documents chip opens the combined files + bundles dialog */}
            {compareFilesCount === 0 ? (documentsChip ?? documentsButton) : null}

            {compareChip}

            <button type="button" className={styles.quickActionButton} onClick={onOpenPromptDialog}>
              <LibraryBooks fontSize="small" />
              <span>{T.translate('generic.prompt')}</span>
            </button>
          </div>
        )}

        {fileSidebarAskAI && (documentsChip || compareChip) && (
          <>
            {documentsChip}
            {compareChip}
          </>
        )}

        {(showStandingInstructionsToggle || (responseMode && onResponseModeChange)) && (
          <div className={styles.promptControls}>
            {showStandingInstructionsToggle && onUseStandingInstructionsChange && (
              <label className={styles.standingInstructionsToggle}>
                <span>{T.translate('generic.useStandingInstructions')}</span>
                <MuiSwitch
                  size="small"
                  checked={useStandingInstructions}
                  onChange={(_event, checked) => onUseStandingInstructionsChange(checked)}
                  inputProps={{
                    'aria-label': T.translate('generic.useStandingInstructions') as string,
                  }}
                />
              </label>
            )}

            {responseMode && onResponseModeChange && (
              <div className={styles.responseModeToggle}>
                <button
                  type="button"
                  className={`${styles.responseModeButton} ${responseMode === 'fast' ? styles.responseModeButtonActive : ''}`}
                  onClick={() => onResponseModeChange('fast')}
                  aria-pressed={responseMode === 'fast'}
                >
                  <Bolt sx={{ fontSize: 16 }} />
                  <span>{T.translate('generic.fast')}</span>
                </button>
                <button
                  type="button"
                  className={`${styles.responseModeButton} ${responseMode === 'thinking' ? styles.responseModeButtonActive : ''}`}
                  onClick={() => onResponseModeChange('thinking')}
                  aria-pressed={responseMode === 'thinking'}
                >
                  <Psychology sx={{ fontSize: 16 }} />
                  <span>{T.translate('generic.thinking')}</span>
                </button>
              </div>
            )}
          </div>
        )}

        {isStreaming ? (
          <button
            type="button"
            className={`${styles.iconButton} ${styles.stopButton}`}
            aria-label="Stop streaming"
            onClick={onStopStreaming}
          >
            <StopRounded fontSize="small" />
          </button>
        ) : (
          inputValue.trim() && (
            <button
              type="submit"
              className={`${styles.sendButton} ${
                responseMode && onResponseModeChange ? styles.sendButtonWithControls : ''
              }`}
              aria-label="Send message"
            >
              <SendRounded fontSize="small" />
            </button>
          )
        )}
      </div>
    </form>
  );
}
